/**
 * User tracking, split by identity type (SCRUM-1967).
 *
 * Before this change a single `alita_tracking_uuid` cookie held *either* an
 * anonymous id *or* the logged-in account uuid, and the agent session used
 * whichever was present. Only the **anonymous** use was the legal problem: the
 * cookie was `SameSite=None; Secure` and forced into cross-origin customer
 * iframes via the Storage Access API, linking a visitor across every site that
 * embeds an Alita agent (GDPR/ePrivacy/CCPA + WA My Health My Data exposure).
 *
 * So the two identities are now stored separately:
 *
 *  - **Anonymous → partitioned `localStorage`.** Browsers partition third-party
 *    storage per top-level site automatically, so the same registrable domain
 *    resolves to one id and different customer domains get different ids — no
 *    cross-site linkage, no third-party cookie, no Storage Access API.
 *
 *  - **Authenticated → first-party cookie (hub origin).** This was never a legal
 *    problem — it only exists for a logged-in user in the first-party hub, and a
 *    `SameSite=Lax` cookie is never exposed to the cross-site embed iframe.
 *
 * The agent session prefers the authenticated id when present, otherwise the
 * anonymous id — the same precedence as the old single-cookie behaviour.
 */

// Authenticated id — first-party cookie. Anonymous id — partitioned localStorage.
const TRACKING_KEY = 'alita_tracking_uuid';
const AUTHD_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60; // 1 year

// Last-resort fallback for when localStorage is unavailable (disabled, quota
// errors, sandboxed iframe). Keeps the anonymous path returning a stable id for
// the lifetime of the page so every session/ping still sends a valid user_uuid.
// Not persisted — a reload starts fresh.
let memoryFallbackId: string | null = null;

// ---------------------------------------------------------------------------
// Iframe detection
// ---------------------------------------------------------------------------

export function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Authenticated id — first-party cookie (hub origin only)
// ---------------------------------------------------------------------------

function isSecureContext(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.protocol === 'https:') return true;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '::1';
}

function getAuthdCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${TRACKING_KEY}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setAuthdCookie(uuid: string): void {
  if (typeof document === 'undefined') return;
  const parts = [
    `${TRACKING_KEY}=${encodeURIComponent(uuid)}`,
    'path=/',
    `max-age=${AUTHD_COOKIE_MAX_AGE_SECONDS}`,
    // SameSite=Lax (not None): first-party only — never sent to the cross-site
    // embed iframe, so the authenticated id can't leak across customer sites.
    'SameSite=Lax',
  ];
  if (isSecureContext()) parts.push('Secure');
  document.cookie = parts.join('; ');
}

function clearAuthdCookie(): void {
  if (typeof document === 'undefined') return;
  const parts = [`${TRACKING_KEY}=`, 'path=/', 'max-age=0', 'SameSite=Lax'];
  if (isSecureContext()) parts.push('Secure');
  document.cookie = parts.join('; ');
}

// ---------------------------------------------------------------------------
// Anonymous id — partitioned localStorage
// ---------------------------------------------------------------------------

function readAnonId(): string | null {
  if (typeof window === 'undefined') return memoryFallbackId;
  try {
    return window.localStorage.getItem(TRACKING_KEY) || memoryFallbackId;
  } catch {
    // Storage blocked — fall back to the in-memory id.
    return memoryFallbackId;
  }
}

function writeAnonId(uuid: string): void {
  // Always mirror to memory so a subsequent read succeeds even if persistence fails.
  memoryFallbackId = uuid;
  if (typeof window === 'undefined') return;
  try {
    // Held indefinitely — a returning visitor to the same site keeps the same id.
    window.localStorage.setItem(TRACKING_KEY, uuid);
  } catch {
    // Storage unavailable/full — the in-memory fallback above covers this page load.
  }
}

// ---------------------------------------------------------------------------
// UUID generation
// ---------------------------------------------------------------------------

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// Public tracking API
// ---------------------------------------------------------------------------

/** The currently effective tracking id — authenticated if present, else anonymous. */
export function getTrackingId(): string | null {
  return getAuthdCookie() ?? readAnonId();
}

/** Stores the logged-in user UUID in the first-party authenticated cookie. */
export function setLoggedInUserId(userUuid: string): void {
  setAuthdCookie(userUuid);
}

/** Clears the authenticated cookie (e.g. on logout). Anonymous id is untouched. */
export function clearLoggedInUserId(): void {
  clearAuthdCookie();
}

/**
 * Resolves the tracking UUID to use for this session.
 *
 * Priority:
 *   1. Authenticated id (first-party cookie) — a logged-in user is always tracked
 *      by their account uuid, never an anonymous id.
 *   2. loggedInUserUuid param — passed from auth context; persisted to the cookie.
 *   3. Existing anonymous id (partitioned localStorage).
 *   4. Generate a new anonymous UUID.
 *
 * Always returns a valid UUID (in-memory fallback when storage is unavailable).
 */
export function getOrCreateTrackingUserId(loggedInUserUuid?: string | null): string {
  const authdId = getAuthdCookie();
  if (authdId) return authdId;

  if (loggedInUserUuid) {
    setAuthdCookie(loggedInUserUuid);
    return loggedInUserUuid;
  }

  const anonId = readAnonId();
  if (anonId) return anonId;

  const newId = generateUUID();
  writeAnonId(newId);
  return newId;
}
